import React, { useRef } from "react"

import { usePostHog } from "posthog-react-native"
import { useMMKVNumber } from "react-native-mmkv"

import { useAudioMuted } from "src/hooks/useAudioMuted"
import { useCurrentMsk } from "src/queries/useConfig"
import { useCurrentConversation } from "src/queries/useConversation"
import { logSentryBreadcrumb } from "src/utils/crashReporting"
import { useAuth } from "./AuthContext"
import { useChannelBrand } from "./ChannelBrandContext"
import { AudioChunkT } from "./ChatContext"
import { useMicrophone } from "./MicrophoneContext"
import { useResponseTiming } from "./ResponseTimingContext"
import { useRiveContext } from "./RiveContext"

export interface VisemeContextType {
  queueSpeech: (audioChunk: AudioChunkT) => void
  stopSpeech: () => Promise<void>
  signalAllAudioReceived: () => void
  isSpeaking: boolean
  isAudioExpected: boolean
  isMuted: boolean
  setMuted: (muted: boolean) => void
  setPlaybackRate: (rate: number) => void
  waitForSpeechQueueEmpty: () => Promise<void>
  /** True when the browser's autoplay policy is blocking audio (Safari only). */
  isAudioBlocked: boolean
}

export const VisemeContext = React.createContext<VisemeContextType | null>(null)

/**
 * Viseme Context (Web version):
 * Provides a context for managing visemes and audio playback using HTML5 Audio API.
 *
 * This context bridges the chat context and the Unity context by providing a way to queue up
 * audio chunks to play and then sends the viseme data to Unity when playing each chunk.
 *
 * returns:
 * - queueSpeech: function to queue up an audio chunk to play
 * - stopSpeech: function to stop the current audio and clear the audio queue
 * - isSpeaking: boolean indicating if the Sidekick is currently speaking
 */
export const VisemeProvider = ({ children }: React.PropsWithChildren) => {
  const { isAuthenticated } = useAuth()
  const { shouldShowSplash } = useChannelBrand()

  // Safari-specific audio unlocking
  const isSafari = navigator.userAgent.includes("Safari") && !navigator.userAgent.includes("Chrome")
  const [audioUnlocked, setAudioUnlocked] = React.useState(!isSafari)
  const audioContextRef = useRef<AudioContext | null>(null)

  const posthog = usePostHog()
  const { data: currentConversation } = useCurrentConversation()
  const { consumeUserStopTiming, currentLLMId, cancelTimingOnError, clearTimingData } =
    useResponseTiming()
  const { handleAnimateRive, stopRiveAnimation, isRiveReady } = useRiveContext()
  const currentMsk = useCurrentMsk()
  const { isListening } = useMicrophone()

  // Use refs for Rive context values to stabilize playAudio and other callbacks
  const handleAnimateRiveRef = React.useRef(handleAnimateRive)
  const stopRiveAnimationRef = React.useRef(stopRiveAnimation)
  const isRiveReadyRef = React.useRef(isRiveReady)

  React.useEffect(() => {
    handleAnimateRiveRef.current = handleAnimateRive
    stopRiveAnimationRef.current = stopRiveAnimation
    isRiveReadyRef.current = isRiveReady
  }, [handleAnimateRive, stopRiveAnimation, isRiveReady])

  // A stack of audio chunks to play
  const [audioChunks, setAudioChunks] = React.useState<AudioChunkT[]>([])
  const [playbackRate = 1.0, setPlaybackRate] = useMMKVNumber("user.audio.playbackRate")
  const { isMuted, setIsMuted } = useAudioMuted()

  // Track whether more audio chunks are expected from the backend
  const [isAudioExpected, setIsAudioExpected] = React.useState(false)
  // Keep a ref of isMuted to avoid restarting audio when mute state changes
  const isMutedRef = React.useRef(isMuted)

  React.useEffect(() => {
    isMutedRef.current = isMuted
  }, [isMuted])

  const currentAudioChunk = React.useRef<HTMLAudioElement | undefined>(undefined)
  const isPlayingAudio = React.useRef(false)
  const isStoppingSpeechRef = React.useRef(false)

  // If Rive isn't ready when a chunk starts playing, we stash that chunk's speech
  // marks here and animate as soon as Rive becomes ready. This prevents the
  // "audio plays but the mouth stays shut" race (e.g. first chunk of a session or
  // just after an avatar reload, when the .riv file hasn't finished loading).
  // Audio still plays immediately regardless — this only governs the optional
  // lip animation.
  const pendingAnimationRef = React.useRef<{
    speechMarks: AudioChunkT["speechMarks"]
    startedAt: number
  } | null>(null)

  // Stable helper so it can be shared by playAudio and the Rive-ready effect
  // without recreating either callback.
  const triggerRiveAnimation = React.useCallback(
    (speechMarks: AudioChunkT["speechMarks"], offsetMs = 0) => {
      if (!handleAnimateRiveRef.current) return
      try {
        handleAnimateRiveRef.current(speechMarks, offsetMs)
      } catch (animationError) {
        console.error("[VISEME_CONTEXT WEB] Animation failed:", animationError)
        // Don't let animation errors affect audio playback
      }
    },
    [],
  )

  const currentAudioChunkData = audioChunks[0]
  // Keep a ref to avoid recreating playAudio callback when chunk data changes
  const currentAudioChunkDataRef = React.useRef(currentAudioChunkData)
  React.useEffect(() => {
    currentAudioChunkDataRef.current = currentAudioChunkData
  }, [currentAudioChunkData])

  // Safari audio unlock - trigger on any user gesture
  React.useEffect(() => {
    if (!isSafari || audioUnlocked) return

    const handleUserGesture = () => {
      console.log("[VISEME_CONTEXT WEB] Unlocking Safari audio...")

      // IMPORTANT: play() must be called with NO awaits before it.
      // iOS propagates user activation only within the same synchronous call
      // stack as the touch event. Every await/microtask boundary before play()
      // risks losing the activation window on older iOS versions, causing
      // NotAllowedError even after the gesture. resume() is fire-and-forget;
      // play() is called synchronously and its result handled via .then/.catch.
      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext ||
            (window as any).webkitAudioContext)()
        }
        audioContextRef.current.resume().catch(() => {})

        const silentAudio = new Audio()
        silentAudio.src =
          "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2+LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1+LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwjBiR1wOnNfC0FJHfH8N2QQAoUXrTp66hVFApGn+DyvmwjBiR1wOnNfC0FJHfH8N2QQAoUXrTp66hVFApGn+DyvmwjBiR1wOnNfC0FJHfH8N2QQAoUXrTp66hVFA=="

        // volume=1 so iOS counts this as real audio playback and grants sticky
        // activation. We route through a GainNode(0) so no sound reaches speakers.
        silentAudio.volume = 1
        const unlockSource = audioContextRef.current.createMediaElementSource(silentAudio)
        const unlockGain = audioContextRef.current.createGain()
        unlockGain.gain.value = 0
        unlockSource.connect(unlockGain)
        unlockGain.connect(audioContextRef.current.destination)

        silentAudio.onplay = () => {
          console.log("[VISEME_CONTEXT WEB] Safari audio unlock: play started")
        }
        silentAudio
          .play()
          .then(() => {
            console.log("[VISEME_CONTEXT WEB] Safari audio unlocked!")
            setAudioUnlocked(true)
          })
          .catch((error) => {
            console.warn(
              "[VISEME_CONTEXT WEB] Audio unlock failed — if this keeps happening, check iOS Settings > Safari > Website Settings and reset media permissions for this site:",
              error,
            )
          })
      } catch (error) {
        console.warn("[VISEME_CONTEXT WEB] Audio unlock setup failed:", error)
      }
    }

    document.addEventListener("touchstart", handleUserGesture, { once: true, passive: true })
    document.addEventListener("mousedown", handleUserGesture, { once: true })

    return () => {
      document.removeEventListener("touchstart", handleUserGesture)
      document.removeEventListener("mousedown", handleUserGesture)
    }
  }, [isSafari, audioUnlocked])

  const stop = React.useCallback(
    async (resetChunks = false, keepLock = false, audioToUnload?: HTMLAudioElement) => {
      const audio = audioToUnload || currentAudioChunk.current

      // Fix 1: When clearing the queue, always stop Rive (APP-217)
      if (resetChunks && stopRiveAnimationRef.current) {
        stopRiveAnimationRef.current()
      }
      // Stop Rive animation if this is the current audio (non-reset path)
      else if (audio === currentAudioChunk.current && stopRiveAnimationRef.current) {
        stopRiveAnimationRef.current()
      }

      // Stop and unload the audio file.
      try {
        if (audio) {
          audio.pause()
          audio.currentTime = 0
          audio.src = "" // Clear the source to free memory
        }
      } catch (error) {
        // Fix 4 (APP-217): "not loaded" / expected cleanup errors - don't log as error
        const msg = error instanceof Error ? error.message : String(error)
        if (!msg.toLowerCase().includes("not loaded")) {
          console.log("Error stopping and unloading sound", error)
          logSentryBreadcrumb(`Error stopping and unloading sound: ${error}`)
        }
      }

      // Only clear the current reference and lock if this is still the current audio
      if (audio === currentAudioChunk.current) {
        currentAudioChunk.current = undefined
        if (!keepLock) {
          console.log("[VISEME_CONTEXT WEB] Setting isPlayingAudio to false from stop function")
          isPlayingAudio.current = false
        }
      }

      // Fix 1 + Fix 3: When resetChunks, always clear ref/lock and stop any current (raced) audio
      if (resetChunks) {
        const currentNow = currentAudioChunk.current
        if (currentNow) {
          try {
            currentNow.pause()
            currentNow.currentTime = 0
            currentNow.src = ""
          } catch (e) {
            const msg = e instanceof Error ? e.message : String(e)
            if (!msg.toLowerCase().includes("not loaded")) {
              console.log("Error stopping and unloading sound", e)
              logSentryBreadcrumb(`Error stopping and unloading sound: ${e}`)
            }
          }
        }
        currentAudioChunk.current = undefined
        isPlayingAudio.current = false
        pendingAnimationRef.current = null
        setAudioChunks([])
      }
    },
    [],
  )

  // Event handlers - these now track which specific audio element triggered them
  const handleAudioEnded = React.useCallback(
    async (audioElement: HTMLAudioElement) => {
      // Only process if this is still the current audio element
      if (audioElement === currentAudioChunk.current) {
        console.log("[VISEME_CONTEXT WEB] Audio ended")
        // Clear the playing flag first so the useEffect can start the next one immediately
        isPlayingAudio.current = false
        // Slice off the first audio chunk. The useEffect will handle the next one.
        setAudioChunks((prev) => (prev.length > 0 ? prev.slice(1) : prev))
        // Stop and clean up this specific audio element
        await stop(false, false, audioElement)
      }
    },
    [stop],
  )

  const handleAudioError = React.useCallback((event: Event, audioElement: HTMLAudioElement) => {
    // Only process if this is still the current audio element
    if (audioElement === currentAudioChunk.current) {
      const target = event.target as HTMLAudioElement
      console.error("[VISEME_CONTEXT WEB] Audio playback error:", target.error)
      logSentryBreadcrumb(`[VISEME_CONTEXT WEB] Error playing audio: ${target.error?.message}`)
      setAudioChunks((prev) => (prev.length > 0 ? prev.slice(1) : prev))
    }
  }, [])

  const setMuted = React.useCallback(
    (muted: boolean) => {
      setIsMuted(muted)
      // toggleMuted(muted)
      if (currentAudioChunk.current) {
        currentAudioChunk.current.muted = muted
      }
    },
    [setIsMuted],
  )

  const visemeError = React.useCallback(
    (message: string) => {
      logSentryBreadcrumb(message)
      cancelTimingOnError(`Audio playback error: ${message}`)
      stop() // don't reset the rest of the audio chunks
      // Slice off the first audio chunk. The useEffect will handle the next one.
      setAudioChunks((prev: AudioChunkT[]) => prev.slice(1))
    },
    [cancelTimingOnError, stop],
  )

  React.useEffect(() => {
    // When the user selects a new Personal Sidekick (MSK), stop any currently playing audio.
    // This intentionally watches currentMsk?.avatar_id (the MSK itself) rather than the
    // displayed avatar, so that server-initiated ESK transfers do NOT trigger this stop —
    // only a user-driven MSK switch will.
    if (currentAudioChunk.current) {
      stop(true)
    }
  }, [currentMsk?.avatar_id, stop])

  const playAudio = React.useCallback(async () => {
    // Use ref to get current chunk data to avoid callback recreation
    const chunkData = currentAudioChunkDataRef.current
    if (!chunkData) return

    if (isSafari && !audioUnlocked) {
      console.warn(
        "[VISEME_CONTEXT WEB] Safari: Audio not unlocked yet, waiting for user interaction...",
      )
      // Release the lock so playback can retry once audio is unlocked
      isPlayingAudio.current = false
      return
    }

    console.log("[VISEME_CONTEXT WEB] Playing audio", chunkData.id)

    // Clean up any existing audio before creating new one, but keep the lock so useEffect doesn't re-trigger
    if (currentAudioChunk.current) {
      await stop(false, true)
    }

    // Ensure the lock is set
    isPlayingAudio.current = true

    try {
      const audioElement = new Audio()
      audioElement.src = chunkData.audioUrl
        ? chunkData.audioUrl
        : `data:audio/mp3;base64,${chunkData.base64}`
      audioElement.muted = isMutedRef.current
      audioElement.playbackRate = playbackRate
      audioElement.preload = "auto"

      // Fix 2b (APP-217): If stopSpeech ran while we were creating the audio, do not set current or play
      if (isStoppingSpeechRef.current) {
        audioElement.src = ""
        isPlayingAudio.current = false
        return
      }

      // Set as current before adding listeners and playing
      currentAudioChunk.current = audioElement

      // Create closures that capture the specific audio element
      audioElement.addEventListener("ended", () => handleAudioEnded(audioElement))
      audioElement.addEventListener("error", (event) => handleAudioError(event, audioElement))

      await audioElement.play()

      // Animation is optional - audio plays regardless, but we animate if Rive is available
      if (handleAnimateRiveRef.current && chunkData.speechMarks) {
        if (isRiveReadyRef.current) {
          // Rive is ready — animate immediately and clear any stale deferral.
          pendingAnimationRef.current = null
          console.log("[VISEME_CONTEXT WEB] Triggering Rive animation")
          triggerRiveAnimation(chunkData.speechMarks)
        } else {
          // Rive isn't ready yet (load race). Don't drop the lip sync — stash the
          // marks and let the Rive-ready effect fire them once Rive is available.
          console.log("[VISEME_CONTEXT WEB] Rive not ready, deferring animation until ready")
          pendingAnimationRef.current = {
            speechMarks: chunkData.speechMarks,
            startedAt: Date.now(),
          }
        }
      } else {
        console.log("[VISEME_CONTEXT WEB] No animation function or speech marks available", {
          hasAnimateFunction: !!handleAnimateRiveRef.current,
          hasSpeechMarks: !!chunkData.speechMarks,
          isRiveReady: isRiveReadyRef.current,
        })
      }
    } catch (error) {
      // Check if this is an abort error (audio was interrupted intentionally)
      if (error instanceof DOMException && error.name === "AbortError") {
        // This is normal when switching chunks quickly, silently ignore
        return
      }
      // If Safari's autoplay policy blocked us, reset the unlock flag so the
      // next user gesture re-triggers the unlock sequence. Without this reset,
      // audioUnlocked stays true, the gesture listeners never re-register, and
      // the only recovery is a full browser restart.
      if (isSafari && error instanceof DOMException && error.name === "NotAllowedError") {
        console.warn(
          "[VISEME_CONTEXT WEB] playAudio blocked by autoplay policy — resetting unlock state for retry",
        )
        setAudioUnlocked(false)
        isPlayingAudio.current = false
        return
      }
      // For other errors, log and report
      console.error("[VISEME_CONTEXT WEB] Error in playAudio:", error)
      visemeError(`[VISEME_CONTEXT WEB] Error playing audio: ${error}`)
    }
  }, [
    isSafari,
    audioUnlocked,
    playbackRate,
    stop,
    handleAudioEnded,
    handleAudioError,
    visemeError,
    triggerRiveAnimation,
  ])

  // When Rive transitions to ready while a chunk is already playing, fire the
  // animation we deferred at audio start. This is the core of the fix for the
  // "audio plays but mouth stays shut" race.
  React.useEffect(() => {
    if (!isRiveReady) return
    const pending = pendingAnimationRef.current
    if (!pending) return
    pendingAnimationRef.current = null
    // Only animate if this chunk's audio is still playing. The scheduler skips
    // ahead by offsetMs, so lip sync stays aligned no matter how late Rive loads.
    if (!isPlayingAudio.current) return
    // Prefer the audio element's real playback position; fall back to wall-clock
    // elapsed if it isn't available yet.
    const audioPositionMs = currentAudioChunk.current
      ? currentAudioChunk.current.currentTime * 1000
      : Date.now() - pending.startedAt
    console.log(
      `[VISEME_CONTEXT WEB] Rive became ready, triggering deferred animation (offset ${audioPositionMs}ms)`,
    )
    triggerRiveAnimation(pending.speechMarks, audioPositionMs)
  }, [isRiveReady, triggerRiveAnimation])

  const queueEmptyResolversRef = React.useRef<Array<() => void>>([])

  const resolveQueueEmpty = React.useCallback(() => {
    if (queueEmptyResolversRef.current.length === 0) {
      return
    }
    const resolvers = queueEmptyResolversRef.current
    queueEmptyResolversRef.current = []
    resolvers.forEach((resolve) => resolve())
  }, [])

  const waitForSpeechQueueEmpty = React.useCallback(() => {
    return new Promise<void>((resolve) => {
      if (audioChunks.length === 0) {
        resolve()
        return
      }
      queueEmptyResolversRef.current.push(resolve)
    })
  }, [audioChunks.length])

  React.useEffect(() => {
    return () => {
      resolveQueueEmpty()
    }
  }, [resolveQueueEmpty])

  // Ref to track if we're currently in a speaking session
  const isSpeakingSessionRef = React.useRef(false)

  React.useEffect(() => {
    if (audioChunks.length === 0) {
      resolveQueueEmpty()
      clearTimingData()
    }

    // Abort if the user is not authenticated
    if (!isAuthenticated) {
      if (audioChunks.length === 0) {
        isSpeakingSessionRef.current = false
      }
      return
    }

    // Fix 2 (APP-217): Do not start playback while stopSpeech is in progress
    if (isStoppingSpeechRef.current) {
      return
    }

    // Check if we're already playing audio to prevent race conditions
    if (isPlayingAudio.current) {
      console.log("[VISEME_CONTEXT WEB] playAudio skipped (already running)")
      return
    }

    // Abort if there are zero audio chunks
    if (audioChunks.length === 0) {
      // Reset speaking session when no audio chunks
      isSpeakingSessionRef.current = false
      return
    }
    // Delay playback until the channel splash is dismissed, but keep queued chunks.
    if (shouldShowSplash) {
      return
    }

    // Track when sidekick starts a new speaking session
    if (!isSpeakingSessionRef.current) {
      // Try to consume user stop timing data for response time measurement
      const timingData = currentConversation?.id
        ? consumeUserStopTiming(currentConversation.id)
        : null

      posthog?.capture("sidekick_starts_speaking", {
        method: "new_speaking_session",
        skPlatform: "web",
        conversationId: currentConversation?.id ?? null,
        responseTimeMs: timingData?.responseTime ?? null,
        userStopMethod: timingData?.userStopMethod ?? null,
        llmId: currentLLMId,
      })
      isSpeakingSessionRef.current = true
    }

    // Set the flag and play the audio
    isPlayingAudio.current = true
    playAudio()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally excluding consumeUserStopTiming, currentConversation.id, and currentLLMId to avoid re-triggering audio playback
  }, [audioChunks.length, playAudio, isAuthenticated, posthog, shouldShowSplash])

  const queueSpeech = React.useCallback(
    (chunk: AudioChunkT) => {
      if (isListening) return
      // Mark that we're expecting audio on first chunk
      setIsAudioExpected(true)
      setAudioChunks((prev) => [...prev, chunk])
    },
    [isListening],
  )

  React.useEffect(() => {
    // When the audioQueue is shutting down, stop the audio
    return () => {
      stop(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (currentAudioChunk.current) {
      currentAudioChunk.current.playbackRate = playbackRate
    }
  }, [playbackRate])

  const stopSpeech = React.useCallback(async () => {
    isStoppingSpeechRef.current = true
    try {
      await stop(true)
      // Clear the audio expected flag when stopping
      setIsAudioExpected(false)
    } finally {
      isStoppingSpeechRef.current = false
    }
  }, [stop])

  const signalAllAudioReceived = React.useCallback(() => {
    // Called when speechMarksDone is received - all audio chunks have been sent from backend
    setIsAudioExpected(false)
  }, [])

  const value = React.useMemo(
    () => ({
      queueSpeech,
      stopSpeech,
      signalAllAudioReceived,
      isSpeaking: audioChunks.length > 0 || isAudioExpected, // Include pending audio to prevent race conditions
      isAudioExpected,
      isMuted,
      setMuted,
      setPlaybackRate,
      waitForSpeechQueueEmpty,
      isAudioBlocked: isSafari && !audioUnlocked,
    }),
    [
      queueSpeech,
      stopSpeech,
      signalAllAudioReceived,
      audioChunks.length,
      isAudioExpected,
      isMuted,
      setMuted,
      setPlaybackRate,
      waitForSpeechQueueEmpty,
      isSafari,
      audioUnlocked,
    ],
  )

  return <VisemeContext.Provider value={value}>{children}</VisemeContext.Provider>
}

export const useOptionalVisemes = () => React.useContext(VisemeContext)

export const useVisemes = (): VisemeContextType => {
  const context = useOptionalVisemes()
  if (!context) {
    throw new Error("useVisemes must be used within a VisemeProvider")
  }
  return context
}
